import { createElement, useRef, useCallback } from 'react';
import { CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useGlobalStore } from '@/state/global.state';
import { log } from '@/logger/logger';
import { I18N_KEYS } from '@/language/keys';
import { WarningCircleIcon } from '@/UI/Icons/WarningCircleIcon';
import {
  downloadNodeAsset,
  downloadMultipleAssetsFromNode as downloadMultipleAssetsFromNodeUtil,
  downloadFromMultipleNodes as downloadFromMultipleNodesUtil,
} from '@/components/Recipe/NodeAssetDownloader/downloadUtils';
import { MAX_DOWNLOAD_SIZE_BYTES } from '@/consts/download.consts';
import { type DownloadProgressCallback, DownloadSizeExceededError } from '@/services/BrowserDownloader';
import type { DownloadableAsset } from '@/types/downloadable';

const logger = log.getLogger('useInteractiveDownloader');

// Threshold for showing download progress snackbar
// Downloads smaller than this will happen silently
const SHOW_DOWNLOAD_PROGRESS_THRESHOLD_BYTES = 5 * 1024 * 1024; // 5 MB

export interface DownloadAssetOptions {
  url?: string;
  blob?: Blob;
  fileType: string;
  nodeName: string;
  prefix?: string;
}

export interface UseInteractiveDownloaderReturn {
  /**
   * Downloads a single asset. Handles errors automatically with snackbar notification.
   */
  downloadAssetFromNode: (options: DownloadAssetOptions) => Promise<void>;

  /**
   * Downloads multiple assets from a single node as a ZIP file.
   * Shows progress UI (snackbar with cancel button) for downloads > THRESHOLD.
   * Handles errors automatically with snackbar notification.
   */
  downloadAssetsFromNodeWithProgress: (
    assets: DownloadableAsset[],
    nodeName: string,
    prefix: string,
    batchNumber?: number,
  ) => Promise<void>;

  /**
   * Downloads assets from multiple nodes as a folder structure in a ZIP.
   * Shows progress UI (snackbar with cancel button) for downloads > THRESHOLD.
   * Handles errors automatically with snackbar notification.
   */
  downloadFromMultipleNodesWithProgress: (
    nodesWithAssets: Array<{
      nodeName: string;
      assets: DownloadableAsset[];
    }>,
    prefix: string,
  ) => Promise<void>;
}

/**
 * Hook for interactive file downloads with progress tracking and error handling.
 *
 * Provides download functions that automatically:
 * - Show progress UI (snackbar) for large downloads (> 5MB)
 * - Allow cancellation via cancel button
 * - Display error notifications on failure
 * - Prevent duplicate simultaneous downloads
 *
 * @example
 * const { downloadAsset, downloadAssetsWithProgress } = useInteractiveDownloader();
 *
 * // Single file download
 * await downloadAsset({ url, fileType, nodeName, prefix });
 *
 * // Multiple files with progress
 * await downloadAssetsWithProgress(assets, nodeName, prefix);
 */
export const useInteractiveDownloader = (): UseInteractiveDownloaderReturn => {
  const { t } = useTranslation();
  const { updateSnackbarData, updateSnackbarText } = useGlobalStore();
  const isDownloadingRef = useRef(false);

  const showDownloadError = useCallback(
    (isMultiple: boolean) => {
      updateSnackbarData({
        text: t(
          isMultiple
            ? I18N_KEYS.RECIPE_MAIN.ERRORS.DOWNLOAD_FAILED_MULTIPLE
            : I18N_KEYS.RECIPE_MAIN.ERRORS.DOWNLOAD_FAILED,
        ),
        isOpen: true,
        severity: 'error',
        icon: createElement(WarningCircleIcon, { width: 20, height: 20 }),
        action: null,
        duration: 5000,
      });
    },
    [updateSnackbarData, t],
  );

  const showCancelledSnackbar = useCallback(
    () =>
      updateSnackbarData({
        text: 'Download cancelled',
        action: null,
        icon: null,
        duration: 3000,
        isOpen: true,
        severity: 'info',
      }),
    [updateSnackbarData],
  );

  const showDownloadSizeExceededSnackbar = useCallback(
    (sizeInMB: number, limitInMB: number) => {
      updateSnackbarData({
        text: t(I18N_KEYS.RECIPE_MAIN.ERRORS.DOWNLOAD_SIZE_EXCEEDED, {
          size: `${sizeInMB} MB`,
          limit: `${limitInMB} MB`,
        }),
        isOpen: true,
        severity: 'error',
        icon: createElement(WarningCircleIcon, { width: 20, height: 20 }),
        action: null,
        duration: 5000,
      });
    },
    [updateSnackbarData, t],
  );

  const handleDownloadError = useCallback(
    (downloadId: string, error: unknown, isMultiple: boolean) => {
      if (error instanceof DownloadSizeExceededError) {
        const sizeInMB = Math.round(error.sizeInBytes / 1024 / 1024);
        const limitInMB = Math.round(MAX_DOWNLOAD_SIZE_BYTES / 1024 / 1024);
        showDownloadSizeExceededSnackbar(sizeInMB, limitInMB);
        return;
      }
      if (error instanceof Error && error.name === 'AbortError') {
        logger.info(`[${downloadId}] Download cancelled by user`);
        showCancelledSnackbar();
      } else {
        logger.error(`[${downloadId}] Download failed:`, error);
        showDownloadError(isMultiple);
      }
    },
    [showDownloadSizeExceededSnackbar, showCancelledSnackbar, showDownloadError],
  );

  const createProgressHandler = useCallback(
    (downloadId: string, abortController: AbortController): { showProgressNotifications: DownloadProgressCallback } => {
      let totalBytes = 0;
      let isSnackbarOpen = false;

      function initSnackbar(): void {
        updateSnackbarData({
          isOpen: true,
          minWidth: '260px',
          duration: null,
          severity: 'info',
          icon: <CircularProgress size={16} color="inherit" />,
          action: {
            text: 'Cancel',
            onClick: () => {
              logger.info(`[${downloadId}] User cancelled download`);
              abortController.abort();
            },
          },
        });
        isSnackbarOpen = true;
      }

      const showProgressNotifications: DownloadProgressCallback = (progress) => {
        logger.debug(`[${downloadId}] Progress:`, progress);
        if (progress.totalBytes !== undefined) {
          totalBytes = progress.totalBytes;
          logger.info(`[${downloadId}] Total size: ${(totalBytes / 1024 / 1024).toFixed(2)} MB`);
        }
        if (totalBytes < SHOW_DOWNLOAD_PROGRESS_THRESHOLD_BYTES) return;
        if (!isSnackbarOpen) {
          initSnackbar();
        }
        updateSnackbarText(t(I18N_KEYS.RECIPE_MAIN.DOWNLOAD_PREPARING, { percent: Math.round(progress.percent) }));
      };

      return { showProgressNotifications };
    },
    [updateSnackbarData, updateSnackbarText, t],
  );

  const executeDownloadWithProgress = useCallback(
    async (
      downloadFn: (onProgress: DownloadProgressCallback, signal: AbortSignal) => Promise<void>,
      isMultiple: boolean,
    ) => {
      if (isDownloadingRef.current) {
        logger.warn('Download already in progress, ignoring duplicate call');
        return;
      }

      const downloadId = `download-${Date.now()}`;
      const abortController = new AbortController();
      const { showProgressNotifications } = createProgressHandler(downloadId, abortController);
      isDownloadingRef.current = true;

      try {
        logger.info(`[${downloadId}] Starting download`);
        await downloadFn(showProgressNotifications, abortController.signal);
        logger.info(`[${downloadId}] Download completed successfully`);
        updateSnackbarData({ key: downloadId, isOpen: false });
      } catch (error) {
        handleDownloadError(downloadId, error, isMultiple);
      } finally {
        logger.info(`[${downloadId}] Cleaning up`);
        isDownloadingRef.current = false;
      }
    },
    [handleDownloadError, createProgressHandler, updateSnackbarData],
  );

  const downloadAssetFromNode = useCallback(
    async (options: DownloadAssetOptions) => {
      try {
        await downloadNodeAsset(options);
      } catch (error) {
        logger.error('Single asset download failed:', error);
        showDownloadError(false);
      }
    },
    [showDownloadError],
  );

  const downloadAssetsFromNodeWithProgress = useCallback(
    async (assets: DownloadableAsset[], nodeName: string, prefix: string, batchNumber?: number) => {
      const isMultiple = assets.length > 1;
      await executeDownloadWithProgress(async (onProgress, signal) => {
        await downloadMultipleAssetsFromNodeUtil(assets, nodeName, prefix, batchNumber, onProgress, signal);
      }, isMultiple);
    },
    [executeDownloadWithProgress],
  );

  const downloadFromMultipleNodesWithProgress = useCallback(
    async (
      nodesWithAssets: Array<{
        nodeName: string;
        assets: DownloadableAsset[];
      }>,
      prefix: string,
    ) => {
      await executeDownloadWithProgress(async (onProgress, signal) => {
        await downloadFromMultipleNodesUtil(nodesWithAssets, prefix, onProgress, signal);
      }, true); // Always multiple when downloading from multiple nodes
    },
    [executeDownloadWithProgress],
  );

  return {
    downloadAssetFromNode,
    downloadAssetsFromNodeWithProgress,
    downloadFromMultipleNodesWithProgress,
  };
};
